import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, RefreshCw, Clock, Plus, Send, Trash2, AlertTriangle, X, Snowflake, Pencil, Radio, PackageCheck } from 'lucide-react';
import { PIEZAS, PRECIO_LABELS, MAPA_A_FRIO, PRODUCTOS_FRIOS, CONVERSION } from '../../constants/productos';
import { useAuth } from '../../contexts/AuthContext';
import { ControlRemotoModal } from '../../components/ControlRemotoModal';
import { fmtMoney } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PreciosTable({ data }) {
  const fmt = (val) => {
    if (!val || val === 0) return <span className="text-zinc-300">--</span>;
    return fmtMoney(val, 2);
  };

  const hasAnyPrice = data.some(p => p.precios && Object.values(p.precios).some(v => v > 0));
  if (!hasAnyPrice) return null;

  return (
    <div className="flex flex-col items-center">
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden w-fit">
        <div className="px-2 py-1 border-b border-zinc-200 bg-emerald-800 text-white">
          <span className="font-bold text-xs">PRECIOS ASIGNADOS</span>
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="bg-emerald-700 text-white">
                <th className="text-left px-2 py-1 font-medium whitespace-nowrap">Punto de Venta</th>
                {PIEZAS.map(p => (
                  <th key={p.clave} className="text-center px-1.5 py-1 font-medium whitespace-nowrap">{p.short}</th>
                ))}
                <th className="text-center px-2 py-1 font-medium whitespace-nowrap">Peso prom.</th>
              </tr>
            </thead>
            <tbody>
              {data.map((punto, i) => (
                <tr key={punto.punto_venta_id} className={`border-b border-zinc-100 ${i % 2 === 1 ? 'bg-zinc-50/50' : ''}`}>
                  <td className="px-2 py-1 font-medium whitespace-nowrap text-zinc-800">{punto.punto_venta_nombre}</td>
                  {PIEZAS.map(p => (
                    <td key={p.clave} className="text-right px-1.5 py-1 font-mono text-zinc-700 whitespace-nowrap">
                      {fmt(punto.precios?.[p.clave])}
                    </td>
                  ))}
                  <td className="text-center px-2 py-1 font-mono text-zinc-500">
                    {punto.peso_promedio ? `${Number(punto.peso_promedio).toFixed(2)} kg` : <span className="text-zinc-300">--</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Reusable inventory table component
function InventoryTable({ data, title, titleColor, headerBg, testId, onControlRemoto }) {
  const totals = {};
  PIEZAS.forEach(p => { totals[p.clave] = 0; });
  data.forEach(punto => {
    PIEZAS.forEach(p => { totals[p.clave] += (punto.disponible?.[p.clave] || 0); });
  });

  const formatTime = (isoStr) => {
    if (!isoStr) return '--';
    try {
      return new Date(isoStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return '--'; }
  };

  return (
    <div className="overflow-x-auto">
      <div className="w-fit mx-auto">
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          {title && (
            <div className={`px-2 py-1 border-b border-zinc-200 ${titleColor || 'bg-zinc-100'}`}>
              <span className="font-bold text-xs">{title}</span>
            </div>
          )}
          <table className="text-xs" data-testid={testId} style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className={headerBg || 'bg-zinc-800 text-white'}>
                <th className="text-left px-2 py-1 font-medium">Punto de Venta</th>
                {PIEZAS.map(p => (
                  <th key={p.clave} className="text-center px-1 py-1 font-medium w-7">{p.short}</th>
                ))}
                <th className="text-center px-1 py-1 font-medium w-8">Tot</th>
                <th className="text-center px-1 py-1 font-medium w-9">Ult.</th>
                {onControlRemoto && <th className="text-center px-1 py-1 font-medium w-8"></th>}
              </tr>
            </thead>
            <tbody>
              {data.map((punto, i) => {
                const total = PIEZAS.reduce((s, p) => s + (punto.disponible?.[p.clave] || 0), 0);
                return (
                  <tr key={punto.punto_venta_id} className={`border-b border-zinc-100 ${i % 2 === 1 ? 'bg-zinc-50/50' : ''}`}>
                    <td className="px-2 py-1 font-medium text-zinc-800 leading-tight max-w-[130px]">
                      <div className="truncate">
                        {punto.punto_venta_nombre}
                        {punto.dia_cerrado && <span className="ml-1 text-red-500">(C)</span>}
                        {!punto.tiene_inventario && <span className="ml-1 text-zinc-400">(--)</span>}
                      </div>
                      {punto.vendedor_nombre && (
                        <div className="text-zinc-400 font-normal truncate">{punto.vendedor_nombre}</div>
                      )}
                    </td>
                    {PIEZAS.map(p => {
                      const qty = punto.disponible?.[p.clave] || 0;
                      return (
                        <td key={p.clave} className={`text-center px-1 py-1 font-mono font-bold ${
                          qty > 0 ? 'text-zinc-900' : qty < 0 ? 'text-red-600' : 'text-zinc-300'
                        }`}>
                          {punto.tiene_inventario ? qty : '--'}
                        </td>
                      );
                    })}
                    <td className="text-center px-1 py-1 font-mono font-bold text-zinc-700">
                      {punto.tiene_inventario ? total : '--'}
                    </td>
                    <td className="text-center px-1 py-1 font-mono text-zinc-400 whitespace-nowrap">
                      {formatTime(punto.ultima_actualizacion)}
                    </td>
                    {onControlRemoto && (
                      <td className="text-center px-1 py-1">
                        <button
                          onClick={() => onControlRemoto(punto)}
                          title="Control Remoto"
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-200 text-zinc-400 hover:text-zinc-700 mx-auto"
                        >
                          <Radio className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr className="bg-zinc-200 font-bold">
                <td className="px-2 py-1">TOTAL</td>
                {PIEZAS.map(p => (
                  <td key={p.clave} className="text-center px-1 py-1 font-mono">{totals[p.clave]}</td>
                ))}
                <td className="text-center px-1 py-1 font-mono">
                  {PIEZAS.reduce((s, p) => s + totals[p.clave], 0)}
                </td>
                <td></td>
                {onControlRemoto && <td></td>}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Productos calientes que tienen equivalente frío
const CALIENTES_CONVERTIBLES = [
  { clave: '1',   nombre: 'Pechuga' },
  { clave: '2',   nombre: 'Pierna' },
  { clave: '3.1', nombre: 'Ala' },
];

export function AdminInventarioDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esPropietario = user?.role === 'propietario';

  const [data, setData] = useState([]);
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const [newTraslados, setNewTraslados] = useState([]);
  const [trasladosBD, setTrasladosBD] = useState([]);
  const [submitting, setSubmitting] = useState(null);
  const [repartidores, setRepartidores] = useState([]);

  const [confirmCierre, setConfirmCierre] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  const [cierrePendientes, setCierrePendientes] = useState(null); // { total, entrantes, salientes, items }
  const [cierreError, setCierreError] = useState('');
  const [grupoExpandido, setGrupoExpandido] = useState(null);
  const [confirmandoMasivo, setConfirmandoMasivo] = useState(false);
  const [masivoMsg, setMasivoMsg] = useState('');

  // ========== Control Remoto ==========
  const [controlRemotoPunto, setControlRemotoPunto] = useState(null);
  const [crInitialTab, setCrInitialTab] = useState(null);

  // ========== Conversión a frío ==========
  const [convPuntoId, setConvPuntoId] = useState('');
  const [convCantidades, setConvCantidades] = useState({ '1': '', '2': '', '3.1': '' });
  const [convSubmitting, setConvSubmitting] = useState(false);
  const [convError, setConvError] = useState('');
  const [convOk, setConvOk] = useState('');

  // ========== Ajuste de inventario inicial ==========
  const [ajustePuntoId, setAjustePuntoId] = useState('');
  const [ajusteCantidades, setAjusteCantidades] = useState({});
  const [ajusteSubmitting, setAjusteSubmitting] = useState(false);
  const [ajusteError, setAjusteError] = useState('');
  const [ajusteOk, setAjusteOk] = useState('');
  const [ajusteLoading, setAjusteLoading] = useState(false);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [dashRes, puntosRes, trasRes, repsRes] = await Promise.all([
        axios.get(`${API}/admin/inventario-dashboard`),
        axios.get(`${API}/puntos-venta`),
        axios.get(`${API}/admin/traslados-dia`),
        axios.get(`${API}/repartidores`),
      ]);
      setData([...dashRes.data].sort((a, b) => a.punto_venta_nombre.localeCompare(b.punto_venta_nombre, 'es')));
      setPuntosVenta(puntosRes.data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setTrasladosBD(trasRes.data);
      setRepartidores(repsRes.data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // ========== New traslado management ==========
  const addTraslado = () => {
    setNewTraslados(prev => [...prev, { id: Date.now(), origen_id: '', destino_id: '', repartidor_id: '', productos: {} }]);
  };
  const removeNew = (id) => setNewTraslados(prev => prev.filter(t => t.id !== id));
  const updateNew = (id, field, value) => {
    setNewTraslados(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
  };
  const updateNewProd = (id, clave, value) => {
    setNewTraslados(prev => prev.map(t =>
      t.id === id ? { ...t, productos: { ...t.productos, [clave]: Math.max(0, parseInt(value) || 0) } } : t
    ));
  };

  const submitTraslado = async (traslado) => {
    if (!traslado.origen_id || !traslado.destino_id || traslado.origen_id === traslado.destino_id) return;
    if (!traslado.repartidor_id) return;
    const prods = {};
    for (const [k, v] of Object.entries(traslado.productos)) { if (v > 0) prods[k] = v; }
    if (Object.keys(prods).length === 0) return;

    setSubmitting(traslado.id);
    try {
      await axios.post(`${API}/admin/traslados`, {
        origen_id: traslado.origen_id,
        destino_id: traslado.destino_id,
        repartidor_id: traslado.repartidor_id,
        productos: prods,
      });
      removeNew(traslado.id);
      await loadData(true);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setSubmitting(null);
    }
  };

  const handleEliminar = async (trasladoId) => {
    try {
      await axios.delete(`${API}/admin/traslados/${trasladoId}`);
      await loadData(true);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchCierrePendientes = useCallback(async (puntoId) => {
    try {
      const { data } = await axios.get(`${API}/admin/transitos-pendientes-punto/${puntoId}`);
      return data;
    } catch {
      return { total: 0, entrantes: 0, salientes: 0, items: [] };
    }
  }, []);

  useEffect(() => {
    if (!confirmCierre) {
      setCierrePendientes(null); setCierreError(''); setMasivoMsg(''); setGrupoExpandido(null);
      return;
    }
    let alive = true;
    setCierrePendientes(null);
    setCierreError('');
    setMasivoMsg('');
    setGrupoExpandido(null);
    fetchCierrePendientes(confirmCierre.punto_venta_id).then((d) => { if (alive) setCierrePendientes(d); });
    return () => { alive = false; };
  }, [confirmCierre, fetchCierrePendientes]);

  const handleCerrarSinOperaciones = async (forzar = false) => {
    if (!confirmCierre) return;
    const nBloq = cierrePendientes?.bloqueantes || 0;
    if (nBloq > 0 && !forzar) return;
    setCerrando(true);
    setCierreError('');
    try {
      await axios.post(`${API}/admin/cerrar-turno-sin-operaciones`, {
        punto_venta_id: confirmCierre.punto_venta_id,
        ...(forzar ? { forzar: true } : {}),
      });
      setConfirmCierre(null);
      await loadData(true);
    } catch (err) {
      console.error('Error:', err);
      setCierreError(err.response?.data?.detail || 'No se pudo cerrar el turno');
    } finally {
      setCerrando(false);
    }
  };

  const handleForzarCierre = () => {
    const n = cierrePendientes?.bloqueantes || 0;
    if (!window.confirm(
      `Se cancelarán ${n} tránsito(s) saliente(s) de este turno y luego se cerrará. ` +
      `Queda registrado en la bitácora. ¿Continuar?`
    )) return;
    handleCerrarSinOperaciones(true);
  };

  const handleConfirmarSobrantesMasivo = async () => {
    if (!confirmCierre) return;
    setConfirmandoMasivo(true);
    setCierreError('');
    setMasivoMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/confirmar-sobrantes-corte`, {
        punto_venta_id: confirmCierre.punto_venta_id,
      });
      setMasivoMsg(`${data.confirmados} sobrante(s) de corte confirmado(s).`);
      setCierrePendientes(await fetchCierrePendientes(confirmCierre.punto_venta_id));
      await loadData(true);
    } catch (err) {
      setCierreError(err.response?.data?.detail || 'No se pudieron confirmar los sobrantes');
    } finally {
      setConfirmandoMasivo(false);
    }
  };

  const abrirControlRemotoEntradas = () => {
    if (!confirmCierre) return;
    const p = confirmCierre;
    setConfirmCierre(null);
    setCrInitialTab({ tab: 'movimiento', movTipo: 'entrada' });
    setControlRemotoPunto(p);
  };

  const handleSelectAjustePunto = async (puntoId) => {
    setAjustePuntoId(puntoId);
    setAjusteError('');
    setAjusteOk('');
    setAjusteCantidades({});
    if (!puntoId) return;
    setAjusteLoading(true);
    try {
      const { data } = await axios.get(`${API}/inventario/actual/${puntoId}`);
      if (!data.has_inicial) {
        setAjusteError('Este punto no tiene inventario inicial registrado hoy.');
        setAjusteLoading(false);
        return;
      }
      const inicial = data.productos_iniciales || {};
      const cantidades = {};
      Object.keys(CONVERSION).forEach(clave => {
        cantidades[clave] = inicial[clave] ?? 0;
      });
      setAjusteCantidades(cantidades);
    } catch {
      setAjusteError('Error al cargar el inventario.');
    } finally {
      setAjusteLoading(false);
    }
  };

  const handleGuardarAjuste = async () => {
    setAjusteError('');
    setAjusteOk('');
    if (!ajustePuntoId) return;
    setAjusteSubmitting(true);
    try {
      await axios.post(`${API}/inventario/inicial/ajuste`, {
        punto_venta_id: ajustePuntoId,
        productos: ajusteCantidades,
      });
      setAjusteOk('Ajuste guardado');
      await loadData(true);
    } catch (err) {
      setAjusteError(err.response?.data?.detail || 'Error al guardar ajuste');
    } finally {
      setAjusteSubmitting(false);
    }
  };

  const handleConvertirFrios = async () => {
    setConvError('');
    setConvOk('');
    if (!convPuntoId) { setConvError('Selecciona un punto de venta'); return; }
    const conversiones = {};
    for (const [clave, val] of Object.entries(convCantidades)) {
      const n = parseFloat(val);
      if (n > 0) conversiones[clave] = n;
    }
    if (Object.keys(conversiones).length === 0) { setConvError('Ingresa al menos una cantidad'); return; }
    setConvSubmitting(true);
    try {
      await axios.post(`${API}/admin/convertir-frios`, { punto_venta_id: convPuntoId, conversiones });
      setConvOk('Conversión registrada');
      setConvCantidades({ '1': '', '2': '', '3.1': '' });
      await loadData(true);
    } catch (err) {
      setConvError(err.response?.data?.detail || 'Error al convertir');
    } finally {
      setConvSubmitting(false);
    }
  };

  // ========== Inventario programado ==========
  const getInventarioProgramado = () => {
    const pendientes = trasladosBD.filter(t => t.status === 'programado' || t.status === 'en_transito');
    if (pendientes.length === 0) return null;

    return data.map(punto => {
      const disp = { ...punto.disponible };
      for (const t of pendientes) {
        const prods = t.status === 'en_transito' ? (t.productos_reales_salida || t.productos) : t.productos;
        for (const [clave, qty] of Object.entries(prods || {})) {
          if (t.origen_id === punto.punto_venta_id) {
            disp[clave] = (disp[clave] || 0) - qty;
          }
          if (t.destino_id === punto.punto_venta_id) {
            disp[clave] = (disp[clave] || 0) + qty;
          }
        }
      }
      return { ...punto, disponible: disp };
    });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '--';
    try {
      return new Date(isoStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    } catch { return '--'; }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  const invProgramado = getInventarioProgramado();
  const programados = trasladosBD.filter(t => t.status === 'programado');
  const enTransito = trasladosBD.filter(t => t.status === 'en_transito');
  const pendientesCount = programados.length + enTransito.length;

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="admin-inventario-dashboard">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg" data-testid="btn-back">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Inventario</h1>
              <p className="text-xs text-zinc-500">{new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastRefresh && (
              <span className="text-xs text-zinc-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {lastRefresh.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button onClick={() => loadData(true)} disabled={refreshing} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg" data-testid="btn-refresh">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 px-3 py-4 space-y-4 overflow-x-auto">

        {/* ========== 0. TURNOS ATRASADOS ========== */}
        {data.some(p => p.turno_atrasado) && (
          <div className="bg-white rounded-xl border border-amber-300 overflow-hidden">
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="font-bold text-xs text-amber-800">TURNOS SIN CERRAR DE DÍAS ANTERIORES</span>
            </div>
            {data.filter(p => p.turno_atrasado).map(punto => (
              <div key={punto.punto_venta_id} className="flex items-center justify-between px-3 py-3 border-b border-amber-100 last:border-0">
                <div>
                  <p className="font-bold text-sm text-zinc-800">{punto.punto_venta_nombre}</p>
                  <p className="text-xs text-zinc-500">
                    Turno abierto: {punto.fecha}
                    {punto.vendedor_nombre && ` · ${punto.vendedor_nombre}`}
                  </p>
                </div>
                <button
                  onClick={() => setConfirmCierre(punto)}
                  className="ml-3 shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold"
                >
                  Cerrar sin operaciones
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ========== 1. INVENTARIO ACTUAL ========== */}
        <InventoryTable
          data={data}
          title="INVENTARIO ACTUAL"
          titleColor="bg-zinc-800 text-white"
          testId="inventory-table"
          onControlRemoto={setControlRemotoPunto}
        />

        {/* ========== 1b. PRECIOS ASIGNADOS ========== */}
        <PreciosTable data={data} />

        {/* ========== 2. PROGRAMAR TRASLADOS ========== */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-zinc-900">Programar Traslados</h2>
            <button onClick={addTraslado} className="h-10 px-4 bg-zinc-900 text-white rounded-lg text-sm font-medium flex items-center gap-2" data-testid="btn-add-traslado">
              <Plus className="w-4 h-4" /> Nuevo
            </button>
          </div>

          {newTraslados.map((t, idx) => (
            <div key={t.id} className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid={`new-traslado-${idx}`}>
              <div className="px-3 py-2 bg-zinc-100 border-b border-zinc-200 flex items-center justify-between">
                <span className="font-bold text-sm text-zinc-700">TRASLADO</span>
                <button onClick={() => removeNew(t.id)} className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {/* Repartidor selector */}
              <div className="px-3 py-2 border-b border-zinc-100">
                <select value={t.repartidor_id} onChange={(e) => updateNew(t.id, 'repartidor_id', e.target.value)} className="w-full h-9 px-2 border border-zinc-300 rounded-lg text-sm bg-white" data-testid={`select-repartidor-${idx}`}>
                  <option value="">Seleccionar repartidor...</option>
                  {repartidores.map(r => (<option key={r.id} value={r.id}>{r.name}</option>))}
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50">
                      <th className="text-left px-3 py-2 font-medium text-xs text-zinc-500">ORIGEN</th>
                      {PIEZAS.map(p => (
                        <th key={p.clave} className="text-center px-1 py-2 font-medium text-xs text-zinc-500 w-12">{p.short}</th>
                      ))}
                      <th className="text-left px-3 py-2 font-medium text-xs text-zinc-500">DESTINO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-2 py-2">
                        <select value={t.origen_id} onChange={(e) => updateNew(t.id, 'origen_id', e.target.value)} className="w-full h-9 px-2 border border-zinc-300 rounded-lg text-sm bg-white" data-testid={`select-origen-${idx}`}>
                          <option value="">Origen</option>
                          {puntosVenta.map(p => (<option key={p.id} value={p.id} disabled={p.id === t.destino_id}>{p.name}</option>))}
                        </select>
                      </td>
                      {PIEZAS.map(p => (
                        <td key={p.clave} className="px-1 py-2">
                          <input type="number" inputMode="numeric" min="0" value={t.productos[p.clave] || ''} onChange={(e) => updateNewProd(t.id, p.clave, e.target.value)} className="w-full h-9 text-center border border-zinc-200 rounded text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400" placeholder="0" data-testid={`input-traslado-${idx}-${p.clave}`} />
                        </td>
                      ))}
                      <td className="px-2 py-2">
                        <select value={t.destino_id} onChange={(e) => updateNew(t.id, 'destino_id', e.target.value)} className="w-full h-9 px-2 border border-zinc-300 rounded-lg text-sm bg-white" data-testid={`select-destino-${idx}`}>
                          <option value="">Destino</option>
                          {puntosVenta.map(p => (<option key={p.id} value={p.id} disabled={p.id === t.origen_id}>{p.name}</option>))}
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-zinc-100">
                <button onClick={() => submitTraslado(t)} disabled={!t.origen_id || !t.destino_id || t.origen_id === t.destino_id || !t.repartidor_id || !Object.values(t.productos).some(v => v > 0) || submitting === t.id} className="w-full h-10 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40" data-testid={`btn-submit-traslado-${idx}`}>
                  {submitting === t.id ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Send className="w-4 h-4" /> Programar traslado</>}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ========== 2b. CONVERSIÓN A FRÍO (solo propietario) ========== */}
        {esPropietario && (
          <div className="bg-white rounded-xl border border-cyan-200 overflow-hidden">
            <div className="px-3 py-2 bg-cyan-50 border-b border-cyan-200 flex items-center gap-2">
              <Snowflake className="w-4 h-4 text-cyan-600 shrink-0" />
              <span className="font-bold text-xs text-cyan-800">CONVERSIÓN A FRÍO</span>
            </div>
            <div className="px-3 py-3 space-y-3">
              <p className="text-xs text-zinc-500">
                Convierte piezas calientes que quedaron del día anterior en su equivalente frío.
                Solo puede hacerse antes del corte.
              </p>

              {/* Punto de venta selector */}
              <select
                value={convPuntoId}
                onChange={e => { setConvPuntoId(e.target.value); setConvError(''); setConvOk(''); }}
                className="w-full h-9 px-2 border border-zinc-300 rounded-lg text-sm bg-white"
              >
                <option value="">Seleccionar punto de venta...</option>
                {puntosVenta.filter(p => !data.find(d => d.punto_venta_id === p.id)?.dia_cerrado).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              {/* Disponible actual */}
              {convPuntoId && (() => {
                const puntoData = data.find(d => d.punto_venta_id === convPuntoId);
                if (!puntoData) return null;
                return (
                  <div className="text-xs text-zinc-500 flex gap-4">
                    {CALIENTES_CONVERTIBLES.map(p => (
                      <span key={p.clave}>
                        {p.nombre}: <strong className="text-zinc-800">{puntoData.disponible?.[p.clave] ?? 0}</strong>
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Cantidades a convertir */}
              <div className="grid grid-cols-3 gap-2">
                {CALIENTES_CONVERTIBLES.map(p => {
                  const frio = PRODUCTOS_FRIOS.find(f => f.clave === MAPA_A_FRIO[p.clave]);
                  return (
                    <div key={p.clave} className="space-y-1">
                      <label className="text-xs text-zinc-500 font-medium block">
                        {p.nombre} → {frio?.nombre}
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={convCantidades[p.clave]}
                        onChange={e => setConvCantidades(prev => ({ ...prev, [p.clave]: e.target.value }))}
                        placeholder="0"
                        className="w-full h-9 text-center border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                    </div>
                  );
                })}
              </div>

              {convError && <p className="text-xs text-red-600">{convError}</p>}
              {convOk && <p className="text-xs text-emerald-600 font-medium">{convOk}</p>}

              <button
                onClick={handleConvertirFrios}
                disabled={convSubmitting || !convPuntoId}
                className="w-full h-10 bg-cyan-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {convSubmitting
                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <><Snowflake className="w-4 h-4" /> Convertir a frío</>}
              </button>
            </div>
          </div>
        )}

        {/* ========== 2c. AJUSTE DE INVENTARIO INICIAL (solo propietario) ========== */}
        {esPropietario && (
          <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
              <Pencil className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="font-bold text-xs text-amber-800">AJUSTE DE INVENTARIO INICIAL</span>
            </div>
            <div className="px-3 py-3 space-y-3">
              <p className="text-xs text-zinc-500">
                Corrige las cantidades del inventario inicial registrado hoy en un punto de venta.
              </p>

              <select
                value={ajustePuntoId}
                onChange={e => handleSelectAjustePunto(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 rounded-lg text-sm bg-white"
              >
                <option value="">Seleccionar punto de venta...</option>
                {puntosVenta.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              {ajusteLoading && (
                <div className="flex justify-center py-2">
                  <span className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                </div>
              )}

              {!ajusteLoading && ajustePuntoId && Object.keys(ajusteCantidades).length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(CONVERSION).map(([clave, info]) => (
                    <div key={clave} className="space-y-0.5">
                      <label className="text-xs text-zinc-500 font-medium block">{info.nombre}</label>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setAjusteCantidades(prev => ({ ...prev, [clave]: Math.max(0, (prev[clave] || 0) - 1) }))}
                          className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded text-zinc-600 active:bg-zinc-100"
                        >-</button>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          value={ajusteCantidades[clave] === 0 ? '' : ajusteCantidades[clave]}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            setAjusteCantidades(prev => ({ ...prev, [clave]: isNaN(val) || val < 0 ? 0 : val }));
                          }}
                          onBlur={e => {
                            if (e.target.value === '') setAjusteCantidades(prev => ({ ...prev, [clave]: 0 }));
                          }}
                          placeholder="0"
                          className="flex-1 h-8 text-center border border-zinc-300 rounded text-sm font-mono focus:outline-none focus:ring-1 focus:ring-amber-400"
                        />
                        <button
                          onClick={() => setAjusteCantidades(prev => ({ ...prev, [clave]: (prev[clave] || 0) + 1 }))}
                          className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded text-zinc-600 active:bg-zinc-100"
                        >+</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {ajusteError && <p className="text-xs text-red-600">{ajusteError}</p>}
              {ajusteOk && <p className="text-xs text-emerald-600 font-medium">{ajusteOk}</p>}

              {!ajusteLoading && Object.keys(ajusteCantidades).length > 0 && (
                <button
                  onClick={handleGuardarAjuste}
                  disabled={ajusteSubmitting || !ajustePuntoId}
                  className="w-full h-10 bg-amber-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {ajusteSubmitting
                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <><Pencil className="w-4 h-4" /> Guardar ajuste</>}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ========== 3. INVENTARIO PROGRAMADO ========== */}
        {invProgramado && (
          <InventoryTable
            data={invProgramado}
            title={`INVENTARIO PROGRAMADO (con ${pendientesCount} traslado${pendientesCount > 1 ? 's' : ''} pendiente${pendientesCount > 1 ? 's' : ''})`}
            titleColor="bg-blue-100 text-blue-800"
            headerBg="bg-blue-700 text-white"
            testId="inventory-programado-table"
          />
        )}

        {/* ========== 4. GRID DE TRASLADOS ========== */}
        {trasladosBD.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <div className="px-3 py-2 bg-zinc-100 border-b border-zinc-200">
              <span className="font-bold text-sm text-zinc-700">TRASLADOS ({trasladosBD.length})</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="traslados-grid">
                <thead>
                  <tr className="bg-zinc-50 text-zinc-500">
                    <th className="text-left px-3 py-2 font-medium text-xs">Origen</th>
                    <th className="px-1 py-2 w-16"></th>
                    {PIEZAS.map(p => (
                      <th key={p.clave} className="text-center px-1 py-2 font-medium text-xs w-10">{p.short}</th>
                    ))}
                    <th className="text-left px-3 py-2 font-medium text-xs">Destino</th>
                    <th className="text-left px-2 py-2 font-medium text-xs">Rep.</th>
                    <th className="text-center px-2 py-2 font-medium text-xs w-24">Estatus</th>
                    <th className="text-center px-2 py-2 font-medium text-xs w-20">Hora</th>
                    <th className="text-center px-2 py-2 font-medium text-xs w-14"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...trasladosBD].sort((a, b) => (b.programado_at || '').localeCompare(a.programado_at || '')).map((t, i) => {
                    const hasSalida = !!t.productos_reales_salida;
                    const hasEntrada = !!t.productos_reales_entrada;
                    const rowSpan = 1 + (hasSalida ? 1 : 0) + (hasEntrada ? 1 : 0);
                    const rowBg = i % 2 === 1 ? 'bg-zinc-50/50' : '';
                    const statusBadge = t.status === 'procesado' || t.status === 'realizado' ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Completado</span>
                    ) : t.status === 'en_transito' ? (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">En tránsito</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Programado</span>
                    );
                    const hora = t.status === 'procesado' || t.status === 'realizado'
                      ? formatTime(t.entrada_confirmada_at || t.realizado_at)
                      : t.status === 'en_transito'
                        ? formatTime(t.salida_confirmada_at)
                        : formatTime(t.programado_at);

                    return [
                      /* ---- Row 1: Programado ---- */
                      <tr key={`${t.id}-prog`} className={`border-b border-zinc-100 ${rowBg}`}>
                        <td rowSpan={rowSpan} className="px-3 py-2 font-medium whitespace-nowrap text-zinc-700 border-r border-zinc-100 align-top">{t.origen_nombre}</td>
                        <td className="px-2 py-1.5 text-xs text-zinc-400 font-medium whitespace-nowrap">Prog.</td>
                        {PIEZAS.map(p => {
                          const qty = t.productos?.[p.clave] || 0;
                          return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono text-xs ${qty > 0 ? 'font-bold text-zinc-800' : 'text-zinc-300'}`}>{qty || '--'}</td>;
                        })}
                        <td rowSpan={rowSpan} className="px-3 py-2 font-medium whitespace-nowrap text-zinc-700 border-l border-zinc-100 align-top">{t.destino_nombre}</td>
                        <td rowSpan={rowSpan} className="px-2 py-2 text-xs text-zinc-500 whitespace-nowrap align-top">{t.repartidor_nombre || '--'}</td>
                        <td rowSpan={rowSpan} className="text-center px-2 py-2 align-top">{statusBadge}</td>
                        <td rowSpan={rowSpan} className="text-center px-2 py-2 text-xs text-zinc-400 align-top">{hora}</td>
                        <td rowSpan={rowSpan} className="text-center px-1 py-2 align-top">
                          {t.status === 'programado' && (
                            <button onClick={() => handleEliminar(t.id)} className="w-8 h-8 flex items-center justify-center text-red-400 hover:bg-red-50 rounded" title="Eliminar" data-testid={`btn-eliminar-traslado-${t.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>,

                      /* ---- Row 2: Real entregado (salida) ---- */
                      hasSalida && (
                        <tr key={`${t.id}-salida`} className={`border-b border-zinc-100 ${rowBg}`}>
                          <td className="px-2 py-1.5 text-xs text-orange-600 font-medium whitespace-nowrap bg-orange-50/40">Entregado</td>
                          {PIEZAS.map(p => {
                            const qty = t.productos_reales_salida?.[p.clave] || 0;
                            const prog = t.productos?.[p.clave] || 0;
                            const diff = prog > 0 && qty !== prog;
                            return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono text-xs bg-orange-50/40 ${qty > 0 ? `font-bold ${diff ? 'text-orange-600' : 'text-orange-700'}` : 'text-zinc-300'}`}>{qty || '--'}</td>;
                          })}
                        </tr>
                      ),

                      /* ---- Row 3: Real recibido (entrada) ---- */
                      hasEntrada && (
                        <tr key={`${t.id}-entrada`} className={`border-b border-zinc-200 ${rowBg}`}>
                          <td className="px-2 py-1.5 text-xs text-blue-600 font-medium whitespace-nowrap bg-blue-50/40">Recibido</td>
                          {PIEZAS.map(p => {
                            const qty = t.productos_reales_entrada?.[p.clave] || 0;
                            const prog = t.productos?.[p.clave] || 0;
                            const diff = prog > 0 && qty !== prog;
                            return <td key={p.clave} className={`text-center px-1 py-1.5 font-mono text-xs bg-blue-50/40 ${qty > 0 ? `font-bold ${diff ? 'text-blue-600' : 'text-blue-700'}` : 'text-zinc-300'}`}>{qty || '--'}</td>;
                          })}
                        </tr>
                      ),
                    ].filter(Boolean);
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ========== MODAL: Control Remoto ========== */}
      {controlRemotoPunto && (
        <ControlRemotoModal
          punto={controlRemotoPunto}
          initialTab={crInitialTab?.tab}
          initialMovTipo={crInitialTab?.movTipo}
          onClose={() => { setControlRemotoPunto(null); setCrInitialTab(null); }}
          onRefresh={() => loadData(true)}
        />
      )}

      {/* ========== MODAL: Cerrar turno sin operaciones ========== */}
      {confirmCierre && (() => {
        const sobrantes = PIEZAS.filter(p => (confirmCierre.disponible?.[p.clave] || 0) > 0);
        const totalPiezas = sobrantes.reduce((s, p) => s + (confirmCierre.disponible?.[p.clave] || 0), 0);
        const sumaPz = (prods) => Object.values(prods || {}).reduce((a, b) => a + Number(b || 0), 0);
        const grupos = {};
        (cierrePendientes?.items || []).forEach((it) => {
          const key = `${it.direccion}|${it.contraparte}`;
          if (!grupos[key]) grupos[key] = { key, direccion: it.direccion, contraparte: it.contraparte, n: 0, pz: 0, monto: 0, items: [] };
          grupos[key].n += 1;
          grupos[key].items.push(it);
          if (it.tipo === 'dinero') grupos[key].monto += Number(it.monto || 0);
          else grupos[key].pz += sumaPz(it.productos);
        });
        const filasPend = Object.values(grupos).sort((a, b) => b.n - a.n || a.contraparte.localeCompare(b.contraparte, 'es'));
        const nSobrante = (cierrePendientes?.items || []).filter((i) => i.es_sobrante_corte).length;
        const nDinero = (cierrePendientes?.items || []).filter((i) => i.tipo === 'dinero').length;
        const nBloq = cierrePendientes?.bloqueantes || 0;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl flex flex-col max-h-[85vh]">
              <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <h3 className="font-bold text-zinc-900">Cerrar turno atrasado</h3>
                </div>
                <button onClick={() => setConfirmCierre(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-5 pb-3 space-y-3 overflow-y-auto flex-1">
                <p className="text-sm text-zinc-600">
                  Turno del <strong>{confirmCierre.fecha}</strong> de{' '}
                  <strong>{confirmCierre.punto_venta_nombre}</strong>
                </p>

                {totalPiezas > 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 space-y-1.5">
                    <p className="text-xs font-bold text-blue-800">
                      Se encontraron {totalPiezas} piezas en inventario:
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {sobrantes.map(p => (
                        <span key={p.clave} className="text-xs text-blue-700">
                          {p.short}: <strong>{confirmCierre.disponible[p.clave]}</strong>
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-blue-700 mt-1">
                      Estas piezas se enviarán automáticamente a CEDIS en tránsito.
                    </p>
                  </div>
                ) : (
                  <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-xs text-zinc-600">
                    No se encontraron existencias en este punto de venta para esa fecha.
                  </div>
                )}

                {cierrePendientes === null ? (
                  <div className="text-xs text-zinc-400 px-1">Revisando tránsitos pendientes…</div>
                ) : cierrePendientes.total > 0 ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 space-y-2">
                    <p className="text-sm font-bold text-red-800 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      {cierrePendientes.total} tránsitos sin confirmar
                    </p>
                    <div className="text-xs text-red-700 space-y-0.5">
                      <div>{cierrePendientes.entrantes} por recibir · {cierrePendientes.salientes} por enviar</div>
                      {nSobrante > 0 && <div>{nSobrante} son sobrantes de corte de otras sucursales</div>}
                      {nDinero > 0 && <div>{nDinero} de dinero</div>}
                      <div className="font-bold pt-0.5">
                        {nBloq > 0
                          ? `${nBloq} bloquean el cierre (salientes de este turno)`
                          : 'Ninguno bloquea el cierre — puedes cerrar'}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      {nSobrante > 0 && (
                        <button
                          onClick={handleConfirmarSobrantesMasivo}
                          disabled={confirmandoMasivo}
                          className="h-9 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                          {confirmandoMasivo
                            ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <PackageCheck className="w-4 h-4" />}
                          Confirmar los {nSobrante} sobrantes de corte
                        </button>
                      )}
                      <button
                        onClick={abrirControlRemotoEntradas}
                        className="h-9 border border-red-300 text-red-700 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-red-100"
                      >
                        Abrir Entradas / Traslados
                      </button>
                    </div>
                    {nSobrante > 0 && (
                      <p className="text-[11px] text-red-600">
                        "Confirmar sobrantes" acepta las cantidades que declaró cada sucursal, sin revisión física.
                      </p>
                    )}
                    {masivoMsg && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 text-xs text-emerald-700">
                        {masivoMsg}
                      </div>
                    )}

                    <div className="max-h-44 overflow-y-auto rounded-lg border border-red-200 bg-white/70 divide-y divide-red-100">
                      {filasPend.map((f) => {
                        const abierto = grupoExpandido === f.key;
                        return (
                          <div key={f.key}>
                            <button
                              onClick={() => setGrupoExpandido(abierto ? null : f.key)}
                              className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs hover:bg-red-50"
                            >
                              <span className="text-red-800 truncate flex items-center gap-1">
                                <span className="text-red-400 w-3">{abierto ? '▾' : '▸'}</span>
                                <span className="text-red-400">{f.direccion === 'entrante' ? '↓' : '↑'}</span>
                                {f.contraparte}
                              </span>
                              <span className="text-red-600 shrink-0 font-mono">
                                {f.n > 1 ? `${f.n}×` : ''}
                                {f.pz > 0 ? ` ${+f.pz.toFixed(1)} pz` : ''}
                                {f.monto > 0 ? ` ${fmtMoney(f.monto)}` : ''}
                              </span>
                            </button>
                            {abierto && (
                              <div className="px-3 pb-1.5 space-y-1">
                                {f.items.map((it, k) => (
                                  <div key={it.id || k} className="text-[11px] text-red-700 bg-red-50/60 rounded px-2 py-1">
                                    <span className="text-red-400">#{(it.id || '').slice(-6)}</span>{' '}
                                    {it.es_sobrante_corte ? 'sobrante de corte · ' : ''}
                                    {it.tipo === 'dinero'
                                      ? fmtMoney(it.monto)
                                      : Object.entries(it.productos || {})
                                          .map(([c, v]) => `${PRECIO_LABELS[c] || c}: ${v}`)
                                          .join(' · ')}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {nBloq > 0 ? (
                      <>
                        <p className="text-xs text-red-700 pt-0.5">
                          Los {nBloq} salientes de este turno caerían en un corte ya hecho.
                          Cancélalos en Traslados, o fuerza el cierre (los cancela y cierra).
                        </p>
                        <button
                          onClick={handleForzarCierre}
                          disabled={cerrando}
                          className="h-9 w-full bg-red-700 hover:bg-red-800 text-white rounded-lg text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                          {cerrando
                            ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : `Forzar cierre (cancela ${nBloq} tránsito${nBloq > 1 ? 's' : ''})`}
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-red-700 pt-0.5">
                        Los entrantes se confirmarán en el turno del destino y los de otras fechas
                        no se afectan: puedes cerrar este turno sin riesgo de descuadre.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800">
                    El punto de venta podrá abrir un nuevo turno normalmente.
                  </div>
                )}

                {cierreError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700">
                    {cierreError}
                  </div>
                )}
              </div>
              <div className="flex gap-3 px-5 pb-5 pt-3 shrink-0 border-t border-zinc-100">
                <button
                  onClick={() => setConfirmCierre(null)}
                  className="flex-1 h-11 bg-zinc-100 rounded-xl font-medium text-sm text-zinc-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleCerrarSinOperaciones(false)}
                  disabled={cerrando || cierrePendientes === null || (cierrePendientes.bloqueantes || 0) > 0}
                  className="flex-1 h-11 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold text-sm disabled:opacity-40 flex items-center justify-center"
                >
                  {cerrando
                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : cierrePendientes && (cierrePendientes.bloqueantes || 0) > 0
                      ? 'Resuelve los salientes primero'
                      : 'Confirmar cierre'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
